/**
 * Draft de cartas de level-up (regras doc 07 §8): 3 cartas distintas por nível,
 * peso 3× no passivo-chave quando a arma correspondente está no nível máx,
 * oferta de evolução quando arma-máx + passivo-chave. Determinístico via rng.
 */
import { WEAPONS } from '../data/index.ts';
import type { Rng } from './rng.ts';
import { bonusNivelPorRaridade, sorteiaRaridade, type Raridade } from './meta.ts';
import { MAX_PASSIVES, MAX_WEAPONS, type HeroSim } from './types.ts';

export type CardKind =
  | { kind: 'new-weapon'; id: string }
  | { kind: 'upgrade-weapon'; id: string }
  | { kind: 'evolve'; id: string; evolucao: string }
  | { kind: 'passive'; id: string };

/** raridade é atribuída só quando o Sortudo está ativo (sortudo>0); ausente = sim base. */
export type Card = CardKind & { rarity?: Raridade };

interface Weighted {
  card: Card;
  weight: number;
}

const KEY_PASSIVE_WEIGHT = 3; // peso 3× (doc 07 §8.2)

export function candidatePool(hero: HeroSim): Weighted[] {
  const owned = new Set(hero.weapons.map((w) => w.id));
  const passives = new Set(hero.passives);
  const pool: Weighted[] = [];

  for (const w of hero.weapons) {
    if (w.level < 5) pool.push({ card: { kind: 'upgrade-weapon', id: w.id }, weight: 1 });
    const def = WEAPONS.find((d) => d.id === w.id);
    if (def && w.level >= 5 && passives.has(def.passivoChave) && !w.evolved) {
      pool.push({ card: { kind: 'evolve', id: w.id, evolucao: def.evolucao }, weight: 2 });
    }
  }
  if (owned.size < MAX_WEAPONS) {
    for (const def of WEAPONS) {
      if (!owned.has(def.id)) pool.push({ card: { kind: 'new-weapon', id: def.id }, weight: 1 });
    }
  }
  if (passives.size < MAX_PASSIVES) {
    // passivos-chave de armas já no nível máx (sem passivo ainda) ganham peso 3×
    const maxedKeys = new Set(
      hero.weapons
        .filter((w) => w.level >= 5)
        .map((w) => WEAPONS.find((d) => d.id === w.id)?.passivoChave)
        .filter((x): x is string => !!x),
    );
    for (const def of WEAPONS) {
      const pid = def.passivoChave;
      if (passives.has(pid)) continue;
      const weight = maxedKeys.has(pid) ? KEY_PASSIVE_WEIGHT : 1;
      // evita duplicar o mesmo passivo id no pool
      if (!pool.some((p) => p.card.kind === 'passive' && p.card.id === pid)) {
        pool.push({ card: { kind: 'passive', id: pid }, weight });
      }
    }
  }
  return pool;
}

const KEY_PASSIVE_PRIORITY_CHANCE = 0.8; // errata E2 (doc 07 §8.2): garante ≥95% em ≤3 drafts

/**
 * Sorteia até n cartas DISTINTAS. Regra de prioridade (errata E2):
 *  - cartas de EVOLUÇÃO disponíveis são sempre incluídas (o payoff é ofertado quando merecido);
 *  - o PASSIVO-CHAVE de uma arma no nível máx entra com 80% de chance por draft
 *    (peso 3× num pool grande não alcançava os 95% exigidos por AC-M1-03).
 * O resto dos slots é preenchido por peso, sem reposição.
 */
export function generateDraft(hero: HeroSim, rng: Rng, n = 3, sortudo = 0): Card[] {
  const pool = candidatePool(hero);
  const chosen: Card[] = [];
  const usadas = new Set<string>();
  const key = (c: Card): string => `${c.kind}:${'id' in c ? c.id : ''}`;
  const take = (c: Card): void => {
    chosen.push(c);
    usadas.add(key(c));
  };

  // 1. evoluções: sempre entram
  for (const w of pool) {
    if (chosen.length >= n) break;
    if (w.card.kind === 'evolve' && !usadas.has(key(w.card))) take(w.card);
  }
  // 2. passivo-chave de arma máx: 80% de chance (determinístico via rng)
  for (const w of pool) {
    if (chosen.length >= n) break;
    if (w.weight === KEY_PASSIVE_WEIGHT && w.card.kind === 'passive' && !usadas.has(key(w.card))) {
      if (rng.next01() < KEY_PASSIVE_PRIORITY_CHANCE) take(w.card);
    }
  }
  // 3. preenche o resto por peso, sem reposição
  const work = pool.filter((w) => !usadas.has(key(w.card)));
  while (chosen.length < n && work.length > 0) {
    const total = work.reduce((s, w) => s + w.weight, 0);
    let roll = rng.next01() * total;
    let idx = 0;
    for (let i = 0; i < work.length; i++) {
      roll -= work[i]!.weight;
      if (roll <= 0) {
        idx = i;
        break;
      }
    }
    take(work[idx]!.card);
    work.splice(idx, 1);
  }
  // Sortudo: atribui raridade às cartas (só quando ativo — não consome rng no caminho base)
  if (sortudo > 0) for (const c of chosen) c.rarity = sorteiaRaridade(rng, sortudo);
  return chosen;
}

/** aplica a carta escolhida ao herói (muta). Raridade (Sortudo) dá níveis extras à arma. */
export function applyCard(hero: HeroSim, card: Card): void {
  const bonus = bonusNivelPorRaridade(card.rarity); // 0 sem Sortudo (raridade ausente)
  switch (card.kind) {
    case 'new-weapon':
      if (hero.weapons.length < MAX_WEAPONS && !hero.weapons.some((w) => w.id === card.id))
        hero.weapons.push({ id: card.id, level: Math.min(5, 1 + bonus), evolved: false });
      break;
    case 'upgrade-weapon': {
      const w = hero.weapons.find((x) => x.id === card.id);
      if (w && w.level < 5) w.level = Math.min(5, w.level + 1 + bonus);
      break;
    }
    case 'evolve': {
      const w = hero.weapons.find((x) => x.id === card.id);
      if (w) w.evolved = true;
      break;
    }
    case 'passive':
      if (hero.passives.length < MAX_PASSIVES && !hero.passives.includes(card.id))
        hero.passives.push(card.id);
      break;
  }
}

/**
 * Política automática (bots + auto-pick de co-op, doc 07 §8.5):
 * evoluir > completar passivo-chave de arma máx > nova arma > subir arma > passivo qualquer.
 */
export function autoPick(cards: Card[]): Card | null {
  if (cards.length === 0) return null;
  const order = (c: Card): number => {
    switch (c.kind) {
      case 'evolve':
        return 0;
      case 'passive':
        return 1;
      case 'new-weapon':
        return 2;
      case 'upgrade-weapon':
        return 3;
    }
  };
  return [...cards].sort((a, b) => order(a) - order(b))[0] ?? null;
}
